/// <reference types="vite/client" />
/**
 * Axios HTTP Client Configuration
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError } from '../types';
import { useAuthStore } from '../stores/auth.store';

// Config - Vite will replace import.meta.env.VITE_API_BASE_URL at build time
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8069';

// Debug: Log API base URL
console.log('[API Client] Base URL:', API_BASE_URL);

const API_TIMEOUT = 30000;

// Auth token storage
let lineUserId: string | null = null;

/**
 * Set LINE User ID
 */
export function setLineUserId(userId: string | null): void {
  lineUserId = userId;
  if (userId) {
    localStorage.setItem('line_user_id', userId);
  } else {
    localStorage.removeItem('line_user_id');
  }
}

/**
 * Get LINE User ID
 */
export function getLineUserId(): string | null {
  if (!lineUserId && typeof localStorage !== 'undefined') {
    lineUserId = localStorage.getItem('line_user_id');
  }
  return lineUserId;
}

/**
 * Clear auth state
 */
export function clearAuth(): void {
  lineUserId = null;
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('line_user_id');
  }
}

/**
 * Create Axios instance with default config
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add LINE User ID header
      const userId = getLineUserId();
      if (userId) {
        config.headers.set('X-Line-User-Id', userId);
        config.headers.set('X-Auth-Provider', 'line');
      }

      const companyId = useAuthStore.getState().currentCompanyId;
      if (companyId) {
        config.headers.set('X-Company-Id', String(companyId));
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError<ApiResponse>) => {
      if (error.response) {
        const { status, data } = error.response;

        // Handle 401 Unauthorized
        if (status === 401) {
          clearAuth();
          // Dispatch event for UI to handle
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
        }

        // Return formatted error
        const apiError: ApiError = data?.error || {
          code: `HTTP_${status}`,
          message: error.message || 'An error occurred',
        };

        return Promise.reject(apiError);
      }

      // Network error
      const networkError: ApiError = {
        code: 'NETWORK_ERROR',
        message: '網路連線失敗，請稍後再試',
      };

      return Promise.reject(networkError);
    }
  );

  return client;
}

// Export singleton instance
export const apiClient = createApiClient();

/**
 * Generic API request helper
 */
export async function apiRequest<T>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: unknown;
    params?: Record<string, unknown>;
  } = {}
): Promise<ApiResponse<T>> {
  const { method = 'GET', data, params } = options;

  try {
    const response = await apiClient.request<ApiResponse<T>>({
      url: endpoint,
      method,
      data,
      params,
    });

    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    return {
      success: false,
      error: apiError,
    };
  }
}

export default apiClient;
